import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAppInstance } from '@helios/auth-firebase-web';

export interface UploadParams {
  orgId: string;
  patientId: string;
  clinicId?: string | null;
  appointmentId?: string | null;
  queueId?: string | null;
  file: File;
  /** ID pre-generated by the caller (used as folder name in Storage). */
  fileId: string;
}

export interface UploadResult {
  downloadUrl: string;
  storagePath: string;
  filename: string;
  mimeType: string;
}

/**
 * Upload a file to Firebase Storage under the patient's folder.
 * Path: orgs/{orgId}/patients/{patientId}/files/{fileId}/{originalFilename}
 */
export async function uploadToStorage(params: UploadParams): Promise<UploadResult> {
  const { orgId, patientId, file, fileId } = params;

  const storagePath = `orgs/${orgId}/patients/${patientId}/files/${fileId}/${file.name}`;
  return uploadFileToPath(storagePath, file);
}

// ─── Scoped Upload (Option B) ───

export type UploadScope = 'patients' | 'users' | 'providers' | 'templates' | 'inventory';

export interface ScopedUploadParams {
  orgId: string;
  scope: UploadScope;
  scopeId: string;
  file: File;
  fileId: string;
  /** Sub-folder within the scope. Default: 'files' */
  folder?: string;
}

/**
 * Upload a file to Firebase Storage under a scoped path.
 * Path: orgs/{orgId}/{scope}/{scopeId}/{folder}/{fileId}/{filename}
 */
export async function uploadToStorageScoped(params: ScopedUploadParams): Promise<UploadResult> {
  const { orgId, scope, scopeId, file, fileId, folder = 'files' } = params;

  const storagePath = `orgs/${orgId}/${scope}/${scopeId}/${folder}/${fileId}/${file.name}`;
  return uploadFileToPath(storagePath, file);
}

// ─── Internal ───

async function uploadFileToPath(storagePath: string, file: File): Promise<UploadResult> {
  const storage = getStorage(getAppInstance());
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: file.type || 'application/octet-stream',
  });

  const downloadUrl = await getDownloadURL(storageRef);

  return {
    downloadUrl,
    storagePath,
    filename: file.name,
    mimeType: file.type || 'application/octet-stream',
  };
}
